import { Container, Graphics, InteractionEvent, RenderTexture, Sprite } from "pixi.js"
import { EntityId } from "game-common/models"
import { GuiManager } from "../gui_manager"
import { isMobile } from "../../../../client_util"
import { CloseButton } from "./close_button"
import { MapSizeButton } from "./map_size_button"

// Picture-in-picture "follow cam" opened from the ember roster. Shows the world rendered live,
// centred on a chosen entity, so the player can watch a defender/forager/lord wherever it is in
// the room. The actual world render into `texture` is driven each frame by PixiJsClientRenderer
// (renderWorldToTexture); this widget just owns the texture, frame, title and close button.
//
// TUNABLES (verify live): VIEW_W/H is the window size; ZOOM is the world scale shown inside it
// (1.0 shows ~VIEW_W px of world; raise to zoom in). Keep ZOOM's visible span within the client's
// FOCUS_RENDER_RADIUS or entities will pop at the edges.
const VIEW_W = 260
const VIEW_H = 200
const ZOOM = 1.0
// The overlay renders into the PiP at scale zoom/surfaceZoom (0.5), which pixel-drops bitmap text.
// Render the texture at a higher resolution (2 x devicePixelRatio) so the downscaled text stays legible.
const PIP_RESOLUTION = 2 * (typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1)
const FRAME = 4
// No title/name header — the PiP is just the framed world view.
const TITLE_H = 0
const CONTROL_RADIUS = isMobile() ? 15 : 8

interface FollowCamDragHandlers {
    onGrab: () => void
    onDragTo: (x: number, y: number) => void
    onRelease: () => void
    onResize?: () => void
}

export class FollowCamModal extends Container {
    private guiManager: GuiManager
    private texture: RenderTexture
    private view: Sprite
    private frame: Graphics
    private panelWidth = 160
    private closeButton: CloseButton
    private sizeButton: MapSizeButton
    private resizeHandle: Graphics
    private isOpen_ = false
    private grabbed = false
    private grabDx = 0
    private grabDy = 0
    private resizing = false
    private resizeStartX = 0
    private resizeStartWidth = 0
    focusEntityId: EntityId | null = null

    constructor(guiManager: GuiManager, private drag?: FollowCamDragHandlers) {
        super()
        this.guiManager = guiManager

        this.texture = RenderTexture.create({ width: VIEW_W, height: VIEW_H, resolution: PIP_RESOLUTION })

        this.frame = new Graphics()
        this.addChild(this.frame)

        this.view = new Sprite(this.texture)
        this.view.x = FRAME
        this.view.y = FRAME + TITLE_H
        this.addChild(this.view)

        this.sizeButton = new MapSizeButton({ radius: CONTROL_RADIUS, onClick: this.cycleSize })
        this.closeButton = new CloseButton({ radius: CONTROL_RADIUS, onClick: () => this.guiManager.closeFollowCam() })
        this.addChild(this.sizeButton, this.closeButton)
        const resizeHandle = new Graphics()
        this.resizeHandle = resizeHandle
        resizeHandle.interactive = true
        resizeHandle.cursor = "nwse-resize"
        resizeHandle.on("pointerdown", (event: InteractionEvent) => {
            if (!this.parent) {
                return
            }
            this.resizing = true
            this.resizeStartX = event.data.getLocalPosition(this.parent).x
            this.resizeStartWidth = this.getWidth()
            this.drag?.onGrab()
        })
        this.addChild(resizeHandle)
        this.setWidth(this.panelWidth)

        this.interactive = true
        this.cursor = "grab"
        this.on("pointerdown", this.onGrab)
        this.on("pointermove", this.onDragMove)
        this.on("pointerup", this.onRelease)
        this.on("pointerupoutside", this.onRelease)
        this.visible = false
    }

    private onGrab = (event: InteractionEvent) => {
        if (event.target !== this || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.grabDx = this.x - pointer.x
        this.grabDy = this.y - pointer.y
        this.grabbed = true
        this.cursor = "grabbing"
        this.drag?.onGrab()
    }

    private onDragMove = (event: InteractionEvent) => {
        if (this.resizing && this.parent) {
            const pointer = event.data.getLocalPosition(this.parent)
            const width = Math.max(160, Math.min(536, this.resizeStartWidth + pointer.x - this.resizeStartX))
            this.setWidth(width)
            this.drag?.onGrab()
            this.drag?.onResize?.()
            return
        }
        if (!this.grabbed || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.drag?.onDragTo(pointer.x + this.grabDx, pointer.y + this.grabDy)
    }

    private onRelease = () => {
        if (!this.grabbed && !this.resizing) {
            return
        }
        this.grabbed = false
        this.resizing = false
        this.cursor = "grab"
        this.drag?.onRelease()
    }

    private drawFrame = () => {
        this.frame.clear()
        this.frame.beginFill(0x05070a, 0.59)
        this.frame.lineStyle(2, 0xffffff, 0.36)
        this.frame.drawRoundedRect(0, 0, this.getWidth(), this.getHeight(), 6)
        this.frame.endFill()
        this.closeButton.position.set(this.getWidth() - CONTROL_RADIUS - 3, CONTROL_RADIUS + 3)
        this.sizeButton.position.set(this.closeButton.x - CONTROL_RADIUS * 2 - 4, this.closeButton.y)
        const grip = this.resizeHandle
        grip.clear()
        grip.lineStyle(2, 0xffffff, 0.36)
        grip.moveTo(-12, 0)
        grip.lineTo(0, -12)
        grip.beginFill(0xffffff, 0.001)
        grip.drawRect(-16, -16, 20, 20)
        grip.endFill()
        grip.position.set(this.getWidth() - FRAME, this.getHeight() - FRAME)
    }

    private setWidth = (width: number) => {
        this.panelWidth = width
        this.view.width = this.getWidth() - FRAME * 2
        this.view.height = this.getHeight() - FRAME * 2
        this.drawFrame()
    }

    private cycleSize = () => {
        const sizes = [160, 268, 536]
        this.setWidth(sizes.find(width => width > this.panelWidth + 1) ?? sizes[0])
        this.drag?.onGrab()
        this.drag?.onResize?.()
        this.drag?.onRelease()
    }

    open = (entityId: EntityId, _name?: string) => {
        this.focusEntityId = entityId
        this.isOpen_ = true
        this.visible = true
    }

    close = () => {
        this.onRelease()
        this.isOpen_ = false
        this.visible = false
        this.focusEntityId = null
    }

    isOpen = () => this.isOpen_
    getTexture = () => this.texture
    getZoom = () => ZOOM

    // Outer frame dimensions (for positioning the modal relative to other HUD elements).
    getWidth = () => this.panelWidth
    getHeight = () => (VIEW_H + FRAME * 2 + TITLE_H) * this.panelWidth / (VIEW_W + FRAME * 2)
}
